export type ThemeMode = 'light' | 'dark';

export interface AppTheme {
  mode: ThemeMode;
  colors: {
    primary: string;
    primaryLight: string;
    primaryDark: string;
    primaryOn: string;
    accent: string;
    secondary: string;
    deep: string;

    bgPage: string;
    bgSurface: string;
    bgSurfaceElevated: string;
    bgMuted: string;
    bgOverlay: string;

    textPrimary: string;
    textSecondary: string;
    textMuted: string;
    textInverse: string;

    border: string;
    borderStrong: string;
    borderFocus: string;

    success: string;
    danger: string;
    dangerBg: string;
    warning: string;
    info: string;

    brandGradient: string;
    shadowAmbient: string;
  };
  font: {
    primary: string;
    secondary: string;
  };
  radius: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
    pill: string;
  };
  shadow: {
    sm: string;
    md: string;
    lg: string;
  };
  spacing: (n: number) => string;
  breakpoints: {
    mobile: string;
    tablet: string;
    desktop: string;
    wide: string;
  };
  transitions: {
    fast: string;
    base: string;
    slow: string;
  };
}

const sharedTokens = {
  font: {
    primary:
      "'Poppins', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
    secondary: "'Open Sans', system-ui, -apple-system, sans-serif",
  },
  radius: {
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    pill: '999px',
  },
  spacing: (n: number) => `${n * 4}px`,
  breakpoints: {
    mobile: '480px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1440px',
  },
  transitions: {
    fast: '120ms ease',
    base: '200ms ease',
    slow: '320ms ease',
  },
};

const brandGradient =
  'linear-gradient(135deg, #055f0e 0%, #077611 45%, #049366 100%)';

export const lightTheme: AppTheme = {
  mode: 'light',
  ...sharedTokens,
  colors: {
    primary: '#077611',
    primaryLight: '#0c9c1a',
    primaryDark: '#055f0e',
    primaryOn: '#ffffff',
    accent: '#e6b400',
    secondary: '#045471',
    deep: '#049366',

    bgPage: '#f6f8f7',
    bgSurface: '#ffffff',
    bgSurfaceElevated: '#ffffff',
    bgMuted: '#eef1f0',
    bgOverlay: 'rgba(14, 26, 20, 0.45)',

    textPrimary: '#0e1a14',
    textSecondary: '#52605a',
    textMuted: '#90998f',
    textInverse: '#ffffff',

    border: '#e2e6e3',
    borderStrong: '#c9d0cb',
    borderFocus: 'rgba(7, 118, 17, 0.35)',

    success: '#077611',
    danger: '#d63030',
    dangerBg: '#fef2f2',
    warning: '#e69a00',
    info: '#045471',

    brandGradient,
    shadowAmbient: 'rgba(15, 23, 42, 0.08)',
  },
  shadow: {
    sm: '0 1px 2px rgba(15, 23, 42, 0.06)',
    md: '0 6px 16px rgba(15, 23, 42, 0.08)',
    lg: '0 22px 48px rgba(15, 23, 42, 0.12)',
  },
};

export const darkTheme: AppTheme = {
  mode: 'dark',
  ...sharedTokens,
  colors: {
    primary: '#22b62f',
    primaryLight: '#3acf48',
    primaryDark: '#1a9024',
    primaryOn: '#03130a',
    accent: '#f0c451',
    secondary: '#5fb1c9',
    deep: '#3fbe92',

    bgPage: '#0d1411',
    bgSurface: '#141c18',
    bgSurfaceElevated: '#1a221e',
    bgMuted: '#1f2823',
    bgOverlay: 'rgba(0, 0, 0, 0.6)',

    textPrimary: '#e7ede9',
    textSecondary: '#a9b4ae',
    textMuted: '#6f7a74',
    textInverse: '#0d1411',

    border: '#2a332e',
    borderStrong: '#3a443e',
    borderFocus: 'rgba(58, 207, 72, 0.32)',

    success: '#22b62f',
    danger: '#f87171',
    dangerBg: 'rgba(248, 113, 113, 0.12)',
    warning: '#f0b14a',
    info: '#5fb1c9',

    brandGradient,
    shadowAmbient: 'rgba(0, 0, 0, 0.55)',
  },
  shadow: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.35)',
    md: '0 8px 20px rgba(0, 0, 0, 0.45)',
    lg: '0 26px 56px rgba(0, 0, 0, 0.55)',
  },
};

export const themes: Record<ThemeMode, AppTheme> = {
  light: lightTheme,
  dark: darkTheme,
};

export const theme = lightTheme;
